import { AxiosError } from 'axios';
import Border from 'components/common/border/Border';
import { BorderColor } from 'components/common/border/Border.type';
import Button from 'components/common/button/Button';
import { ButtonColor } from 'components/common/button/Button.type';
import AlertModal from 'components/common/modal/alert/AlertModal';
import ConfirmModal from 'components/common/modal/confirm/ConfirmModal';
import useContents from 'hooks/feature/content/useContents';
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CONTENTS_BG } from 'styles/background';
import { RoutePath } from 'types/common/paths';
import { PostContent } from 'types/feature/content/postContent';
import { replaceWorkspaceId } from 'utils/common/replaceId';
import ContentAddForm from './form/ContentAddForm';
import { ScreenTypeCode } from 'types/feature/content/contents';

const ScreensaverContentAddContainer = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const { uploadContent } = useContents();
  const location = useLocation();

  const [addForm, setAddForm] = useState<PostContent>({
    workspaceId: '',
    title: '',
    fileType: null,
    screenType: (location.state?.screenType as ScreenTypeCode) || null,
    type: 'KIOSK',
    file: {
      name: '',
      originalName: '',
      delete: false,
    },
  });
  const [openFailPopup, setOpenFailPopup] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [confirmSave, setConfirmSave] = useState(false);
  const [disableSave, setDisableSave] = useState(false);

  const validateForm = () => {
    if (!addForm.title) {
      handleAlert('타이틀을 입력해주세요.');
      return false;
    }

    if (!addForm.screenType) {
      handleAlert('콘텐츠 유형을 선택해주세요.');
      return false;
    }

    if (!addForm.fileType) {
      handleAlert('파일 유형을 선택해주세요.');
      return false;
    }

    if (!addForm.file.name || !addForm.file.originalName) {
      handleAlert('파일을 등록해주세요.');
      return false;
    }

    return true;
  };

  // 저장
  const handleSave = () => {
    if (validateForm()) {
      setConfirmSave(true);
    }
  };

  // 콘텐츠 등록 클릭 시
  const createContent = async () => {
    if (disableSave) return;

    try {
      setDisableSave(true);

      if (workspaceId) {
        const result = await uploadContent(addForm);
        if (result?.result === 'SUCCESS') {
          moveToContentPage();
        }
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status && error.response.status === 400) {
          handleAlert('콘텐츠를 등록하는 중 문제가 발생하였습니다.');
        }
      }
    }
  };

  const moveToContentPage = () => {
    if (workspaceId) {
      navigate(replaceWorkspaceId(RoutePath.content, workspaceId), {
        state: {
          pageIndex: location.state?.pageIndex || 0,
          screenType: addForm.screenType || location.state?.screenType || 'VERTICAL',
          searchWord: location.state?.searchWord || '',
        },
      });
    }
  };

  const handleAlert = (message: string) => {
    setAlertMessage(message);
    setConfirmSave(false);
    setOpenFailPopup(true);
  };

  return (
    <>
      <div className={`${CONTENTS_BG} p-10 flex flex-col items-center gap-6`}>
        <ContentAddForm addForm={addForm} setAddForm={setAddForm} />
        <Border borderColor={BorderColor.gray} />
        <div className='flex gap-2.5'>
          <Button size={120} text='취소' color={ButtonColor.secondary} onClick={moveToContentPage} />
          <Button size={120} text='저장' color={ButtonColor.primary} onClick={handleSave} />
        </div>
      </div>

      {confirmSave && (
        <ConfirmModal
          message='콘텐츠를 등록하시겠습니까?'
          onClickCancel={() => setConfirmSave(false)}
          onClickOk={createContent}
        />
      )}

      {openFailPopup && (
        <AlertModal width='min-w-[380px]' closeModal={() => setOpenFailPopup(false)} message={alertMessage} />
      )}
    </>
  );
};
export default ScreensaverContentAddContainer;
